import authService from "@/api/auth/authApi.js";
import { applyBranding } from "@/helpers/branding.js";

/*
 * Who is signed in, held in memory only.
 *
 * The sign-in itself is an HttpOnly cookie the page cannot read, so nothing
 * about it is kept in localStorage any more. As the app opens it asks the API
 * (restoreSession) whose address this is and who, if anyone, is signed in.
 */
const session = {
  user: null,
  permissions: [],
  roles: [],
  scope: null, // "admin" or "tenant": decided by the address, not by the person
  address: null,
  signedIn: false,
  tenantName: null,
  company: null,
  hasAllPermissions: false,
  // The parts of the CRM the company switched on (Modules & features); menus follow them.
  features: { customer_support: true, internal_tickets: true },
  unknownAddress: null, // the message when this address belongs to nobody
};

// What the old sign-in left behind in the browser; cleared once, on sight.
const LEGACY_KEYS = [
  "auth_token", "auth_scope", "auth_path", "auth_user", "auth_permissions",
  "auth_roles", "auth_tenant_name", "auth_company", "auth_all_permissions",
];

const forgetLegacy = () => {
  try {
    LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage blocked: nothing was kept there either */
  }
};

/** Fills the session from a sign-in or session answer from the API. */
export const setSession = (data = {}) => {
  session.user = data.user ?? null;
  session.permissions = Array.isArray(data.permissions) ? data.permissions : [];
  session.roles = Array.isArray(data.roles) ? data.roles : [];
  session.tenantName = data.tenant_name ?? session.tenantName;
  session.company = data.company ?? session.company;
  applyBranding(session.company);
  session.hasAllPermissions = data.has_all_permissions === true;
  session.features = { customer_support: true, internal_tickets: true, ...(data.features ?? session.features ?? {}) };
  session.signedIn = session.user !== null;
};

/** A module switched on or off under Modules & features: the menus follow at once. */
export const setFeatures = (features = {}) => {
  session.features = { ...session.features, ...features };
};

/** Signs out locally; the address and its company stay known. */
export const clearSession = () => {
  session.user = null;
  session.permissions = [];
  session.roles = [];
  session.hasAllPermissions = false;
  session.signedIn = false;
};

let ready = null;

/**
 * Asks the API whose address this is and whether anybody is signed in at it.
 * Asked once; every caller — the router's first navigation included — waits
 * on the same answer.
 */
export const restoreSession = () => (ready ??= askApi());

/** Resolves once the session is known. */
export const sessionReady = () => restoreSession();

/*
 * Prototype mode: while the CRM screens are designed against sample data there
 * is no API to ask, so a signed-in session is made up here. The side shown
 * follows the address (admin.* is the platform admin) unless the switch in the
 * top bar has picked one. Turned on with VITE_MOCK_SESSION=true.
 */
export const isMockSession = import.meta.env.VITE_MOCK_SESSION === "true";
// The address decides the side: admin.localhost locally, crm-admin.demulla.co.ke on staging.
const mockScope = () => (/^(admin\.|[a-z0-9]+-admin\.)/.test(window.location.hostname) ? "admin" : "tenant");

/** Prototype mode: (re)makes the signed-in sample session, e.g. after signing out. */
export const mockSession = () => {
  session.scope = mockScope();
  session.address = window.location.host;
  session.company = session.scope === "tenant" ? { name: "Demulla Investment Ltd", code: "CRM001" } : null;
  session.tenantName = session.company?.name ?? null;
  session.unknownAddress = null;
  setSession({
    user: { id: 1, name: session.scope === "admin" ? "Platform Admin" : "Jane Wanjiku", email: "demo@deecrm360.test" },
    permissions: [],
    roles: ["admin"],
    has_all_permissions: true,
    tenant_name: session.tenantName,
    company: session.company,
  });
};

const askApi = async () => {
  forgetLegacy();
  if (isMockSession) return mockSession();

  try {
    const response = await authService.sessionApi();
    const place = response?.data;

    // Anything but the API's JSON — usually the app's own page, when the
    // server does not yet pass /api on this address to the API.
    if (!place || typeof place !== "object" || !place.scope) {
      throw new Error(
        `The DeeCRM360 API did not answer at ${window.location.host}/api. ` +
          "The server needs to pass /api on this address to the API (see deploy/staging/README.md)."
      );
    }

    session.scope = place.scope;
    session.address = place.address;
    session.company = place.company ?? null;
    applyBranding(session.company);
    session.tenantName = place.company?.name ?? null;
    session.unknownAddress = null;

    if (place.signed_in) {
      setSession(place.session);
    } else {
      clearSession();
    }
  } catch (error) {
    clearSession();
    session.scope = null;
    session.unknownAddress = error?.message || "DeeCRM360 could not be reached. Check your connection and reload the page.";
  }
};

export const setCompany = (company) => {
  session.company = company ?? null;
  applyBranding(session.company);
};

export const getSession = () => session;
